import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { notificationApi } from '../api/notificationApi';
import { NotificationItem, NotificationPreference } from '../types';
import NotificationListItem from '../components/NotificationListItem';
import { getNotificationDisplayType, normalizeNotificationType } from '../utils/notificationUtils';
import { RootState } from '../store/store';
import { useLanguage } from '../i18n/LocalizationContext';

const normalizeRole = (role: string) => (role || '').toLowerCase().replace(/[\s_-]/g, '');

const getRelatedPath = (notification: NotificationItem): string | null => {
  const refId = notification.referenceId?.trim();
  if (!refId) return null;

  switch (normalizeNotificationType(notification.type)) {
    case 'attendance':
      return `/attendance/${refId}`;
    case 'leave':
      return `/leave/requests/${refId}`;
    case 'regularization':
      return `/attendance/regularization/${refId}`;
    case 'payroll':
      return `/payroll/${refId}`;
    case 'announcement':
      return `/announcements/${refId}`;
    default:
      return `/bgv/assigned-cases/${refId}`;
  }
};

const UserNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const user = useSelector((state: RootState) => state.auth.user);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [createTitle, setCreateTitle] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [createType, setCreateType] = useState('system');
  const [createActionable, setCreateActionable] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [preferences, setPreferences] = useState<NotificationPreference>({
    emailEnabled: false,
    pushEnabled: true,
    smsEnabled: false,
  });
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);

  const isReadFilter = useMemo(() => {
    if (statusFilter === 'read') return true;
    if (statusFilter === 'unread') return false;
    return undefined;
  }, [statusFilter]);

  const filteredItems = useMemo(() => {
    if (typeFilter === 'all') {
      return items;
    }

    return items.filter((item) => normalizeNotificationType(item.type) === typeFilter);
  }, [items, typeFilter]);

  const isEmployeeUser = useMemo(() => {
    const roles = user?.roles ?? [];

    const allowedRoles = ['employee', 'bgvvendor'];

    if (roles.some((role) => allowedRoles.includes(normalizeRole(role)))) {
      return true;
    }

    return allowedRoles.includes(
      normalizeRole(user?.roleName ?? '')
    );
  }, [user?.roleName, user?.roles]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationApi.getMyNotifications({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        type: typeFilter === 'all' ? undefined : typeFilter,
        isRead: isReadFilter,
      });
      setItems(response.items);
      setTotalCount(response.totalCount);
      setUnreadCount(response.unreadCount);
    } catch {
      toast.error(t("legacy.Failed to load notifications."));
    } finally {
      setLoading(false);
    }
  }, [isReadFilter, page, rowsPerPage, t, typeFilter]);

  const loadPreferences = useCallback(async () => {
    try {
      const response = await notificationApi.getMyPreferences();
      setPreferences(response);
    } catch {
      toast.error(t("legacy.Failed to load notification preferences."));
    }
  }, [t]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    void loadPreferences();
  }, [loadPreferences]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      await loadNotifications();
    } catch {
      toast.error(t("notifications.updateFailed"));
    }
  };

  const handleCreate = async () => {
    const title = createTitle.trim();
    const message = createMessage.trim();

    if (!title) {
      toast.error(t("legacy.Title is required."));
      return;
    }

    if (!message) {
      toast.error(t("legacy.Message is required."));
      return;
    }

    setCreating(true);
    try {
      await notificationApi.create({
        title,
        message,
        type: createType,
        isActionable: createActionable,
      });
      toast.success(t("legacy.Notification created successfully."));
      setCreateTitle('');
      setCreateMessage('');
      setCreateType('system');
      setCreateActionable(false);
      setCreateModalOpen(false);
      await loadNotifications();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("legacy.Failed to create notification."));
    } finally {
      setCreating(false);
    }
  };

  const handlePreferenceChange = (field: keyof NotificationPreference) => async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const next = { ...preferences, [field]: checked };
    setPreferences(next);

    try {
      await notificationApi.updateMyPreferences(next);
      toast.success(t("legacy.Preferences updated."));
    } catch {
      toast.error(t("legacy.Failed to update preferences."));
      setPreferences(preferences);
    }
  };

  const handleOpenNotificationDetails = (item: NotificationItem) => {
    const latestItem = items.find((x) => x.id === item.id) ?? item;
    setSelectedNotification(latestItem.isRead ? latestItem : { ...latestItem, isRead: true });
  };

  const handleCloseNotificationDetails = () => {
    setSelectedNotification(null);
  };

  const handleOpenRelatedPage = () => {
    if (!selectedNotification) return;
    const path = getRelatedPath(selectedNotification);
    if (!path) return;
    setSelectedNotification(null);
    navigate(path);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t("notifications.myNotifications")}</Typography>
        {!isEmployeeUser && (
          <Button variant="contained" onClick={() => setCreateModalOpen(true)} sx={{ textTransform: 'none' }}>
            {t("legacy.Create Notification")}
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{t("navigation.notifications")}</Typography>
            <Chip label={t("notifications.unread", { count: unreadCount })} color={unreadCount > 0 ? 'error' : 'default'} size="small" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="type-filter-label">{t("common.type")}</InputLabel>
              <Select
                labelId="type-filter-label"
                value={typeFilter}
                label={t("common.type")}
                onChange={(e: SelectChangeEvent) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t("common.all")}</MenuItem>
                <MenuItem value="attendance">{t("attendance.title")}</MenuItem>
                <MenuItem value="leave">{t("leave.leave")}</MenuItem>
                <MenuItem value="regularization">{t("legacy.Regularization")}</MenuItem>
                <MenuItem value="payroll">{t("navigation.payroll")}</MenuItem>
                <MenuItem value="announcement">{t("legacy.Announcement")}</MenuItem>
                <MenuItem value="system">{t("legacy.System")}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="status-filter-label">{t("common.status")}</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label={t("common.status")}
                onChange={(e: SelectChangeEvent) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t("common.all")}</MenuItem>
                <MenuItem value="unread">{t("notifications.unreadLabel")}</MenuItem>
                <MenuItem value="read">{t("notifications.readLabel")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {filteredItems.length > 0 ? (
          <Stack spacing={0}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 1.5,
                mb: 2,
              }}
            >
              {filteredItems.map((item) => (
                <div key={item.id}>
                  <NotificationListItem
                    item={item}
                    onMarkAsRead={handleMarkAsRead}
                    onOpenDetails={handleOpenNotificationDetails}
                    loading={loading}
                  />
                </div>
              ))}
            </Box>

            {/* Pagination Info */}
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e0e0e0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {t("legacy.Showing {shown} of {total} notifications", { shown: filteredItems.length, total: totalCount })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {page > 0 && (
                    <Button size="small" onClick={() => setPage(page - 1)} sx={{ textTransform: 'none' }}>
                      {t("common.previous")}
                    </Button>
                  )}
                  <Typography variant="caption">
                    {t("legacy.Page {page}", { page: page + 1 })}
                  </Typography>
                  {(page + 1) * rowsPerPage < totalCount && (
                    <Button size="small" onClick={() => setPage(page + 1)} sx={{ textTransform: 'none' }}>
                      {t("common.next")}
                    </Button>
                  )}
                </Stack>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="rows-per-page-label">{t("legacy.Per Page")}</InputLabel>
                  <Select
                    labelId="rows-per-page-label"
                    value={rowsPerPage.toString()}
                    label={t("legacy.Per Page")}
                    onChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="5">5</MenuItem>
                    <MenuItem value="10">10</MenuItem>
                    <MenuItem value="25">25</MenuItem>
                    <MenuItem value="50">50</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t("legacy.No notifications found.")}
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("legacy.Create Notification")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label={t("common.title")}
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              inputProps={{ maxLength: 200 }}
              size="small"
              required
              fullWidth
            />
            <TextField
              label={t("common.message")}
              value={createMessage}
              onChange={(e) => setCreateMessage(e.target.value)}
              inputProps={{ maxLength: 4000 }}
              multiline
              minRows={4}
              required
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={4}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="create-type-modal-label">{t("common.type")}</InputLabel>
                  <Select
                    labelId="create-type-modal-label"
                    value={createType}
                    label={t("common.type")}
                    onChange={(e: SelectChangeEvent) => setCreateType(e.target.value)}
                  >
                    {/* <MenuItem value="attendance">Attendance</MenuItem>
                    <MenuItem value="leave">Leave</MenuItem>
                    <MenuItem value="payroll">Payroll</MenuItem>
                    <MenuItem value="announcement">Announcement</MenuItem> */}
                    <MenuItem value="system">{t("legacy.System")}</MenuItem>
                  </Select>
                </FormControl>

                {/* <FormControlLabel
                  control={
                    <Switch
                      checked={createActionable}
                      onChange={(_, checked) => setCreateActionable(checked)}
                    />
                  }
                  label="Is Actionable"
                /> */}

                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.emailEnabled}
                      onChange={handlePreferenceChange('emailEnabled')}
                    />
                  }
                  label={t("legacy.Send Email Notification")}
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating} sx={{ textTransform: 'none' }}>
            {creating ? `${t("legacy.Creating")}...` : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedNotification)}
        onClose={handleCloseNotificationDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedNotification?.title || t("legacy.Notification")}
        </DialogTitle>
        <DialogContent dividers>
          {selectedNotification && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={t(getNotificationDisplayType(selectedNotification.type))} size="small" color="primary" variant="outlined" />
                {!selectedNotification.isRead && <Chip label={t("notifications.unreadLabel")} size="small" color="warning" />}
              </Stack>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedNotification.message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(selectedNotification.createdAt).toLocaleString()}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotificationDetails}>{t("common.close")}</Button>
          {selectedNotification?.isActionable && getRelatedPath(selectedNotification) && (
            <Button variant="contained" onClick={handleOpenRelatedPage}>
              {t("legacy.Open Related")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserNotificationsPage;
